import AirlinesIcon from '@mui/icons-material/Airlines';
import { useFormikContext } from 'formik';
import React, { ChangeEvent, memo, useCallback } from 'react';

import PaxIcon from '../../../../../../assets/icons/icon-passenger-light.svg?react';
import PlaneIcon from '../../../../../../assets/icons/icon-plane-light.svg?react';
import { FlightTimeType, ResourceType } from '../../../../../../backend';
import { useIntlValue } from '../../../../../../contexts/IntlContext';
import { useCurrentUserClientTimezone } from '../../../../../../contexts/OidcContext/OidcContext.selectors';
import { useDateFnsFormat } from '../../../../../../hooks/useDateFnsFormat';
import { IataIcao } from '../../../../../IataIcao/IataIcao';
import { TimeDisplay } from '../../../../../navigation/ToggleUTCLocal/TimeDisplays/TimeDisplay';
import { convertTimestampToDatetimeString, localeToDateFormat } from '../../../../../time/time.utils';
import { useRmsGanttValue } from '../../../RmsContext';
import { DeleteTimePicker } from './DeleteTimePicker';
import {
    StyledArrivalDataBox,
    StyledDataBox,
    StyledDataZoneBox,
    StyledDateDataBox,
    StyledDepartureDataBox,
    StyledFlightDataBox,
    StyledFlightDataContainer,
    StyledFlightsInformationBox,
    StyledPaxDataBox,
    StyledTitleDataBox,
} from './FlightsInformation.style';
import {
    arrivalTimeTitle,
    departureTimeTitle,
    getTurnaroundArrivalTime,
    getTurnaroundDepartureTime,
} from './FlightsInformation.utils';
import { TurnaroundFormValues } from './TurnaroundForm.utils';

export const FlightsInformation = memo(function FlightsInformation() {
    const turnaround = useRmsGanttValue('selectedTurnarounds')?.[0];
    const resourceType = useRmsGanttValue('resourceType');
    const timezone = useCurrentUserClientTimezone();
    const format = useDateFnsFormat();
    const locale = useIntlValue('locale');

    const { values, setValues } = useFormikContext<TurnaroundFormValues>();

    const isArrivalOnlyProlongated = !!turnaround?.arrivalFlight && !turnaround?.departureFlight;

    const deleteTime = values?.turnaround?.arrivalFlight?.deleteTime;
    const deleteTimeString = convertTimestampToDatetimeString(deleteTime);

    const handleDeleteTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!event.target.value) return;
            const newDeleteTime = Date.parse(event.target.value);
            setValues({
                ...values,
                turnaround: {
                    ...values.turnaround,
                    arrivalFlight: {
                        ...values.turnaround.arrivalFlight,
                        deleteTime: newDeleteTime,
                    },
                },
                resourceTypes: [...values.resourceTypes, ResourceType.PARKING],
            });
        },
        [setValues, values],
    );

    const minDeleteTime = convertTimestampToDatetimeString(turnaround?.arrivalFlight?.aibt);

    const arrivalFlightOccupation =
        resourceType === ResourceType.BOARDING_GATE
            ? turnaround?.arrivalFlight?.actualResourceOccupations?.[ResourceType.PARKING]
            : turnaround?.arrivalFlight?.actualResourceOccupations?.[resourceType];

    const departureFlightOccupation =
        resourceType === ResourceType.BOARDING_GATE
            ? turnaround?.departureFlight?.actualResourceOccupations?.[ResourceType.PARKING]
            : turnaround?.departureFlight?.actualResourceOccupations?.[resourceType];

    const arrivalFlightDate = turnaround?.arrivalFlight?.arrDay;
    const departureFlightDate = turnaround?.departureFlight?.depDay;

    return (
        <StyledFlightsInformationBox>
            <StyledFlightDataBox>
                {turnaround.arrivalFlight && (
                    <StyledFlightDataContainer>
                        <StyledDateDataBox>
                            {turnaround.multiDaysOccupation && arrivalFlightDate ? (
                                format(new Date(arrivalFlightDate), localeToDateFormat[locale])
                            ) : (
                                <>&nbsp;</>
                            )}
                        </StyledDateDataBox>
                        <StyledArrivalDataBox isborderright={turnaround.departureFlight ? 'true' : null}>
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>FL NUMBER</StyledTitleDataBox>
                                <StyledDataBox>
                                    {turnaround.arrivalFlight.flightNumber
                                        ? (turnaround.arrivalFlight.airlineIata ||
                                              turnaround.arrivalFlight.airlineIcao) +
                                          turnaround.arrivalFlight.flightNumber
                                        : turnaround.arrivalFlight.callsign
                                        ? turnaround.arrivalFlight.callsign
                                        : '-'}
                                </StyledDataBox>
                            </StyledDataZoneBox>
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>{arrivalTimeTitle[FlightTimeType.S]}</StyledTitleDataBox>
                                <StyledDataBox>
                                    {turnaround.arrivalFlight.sibt ? (
                                        <TimeDisplay
                                            time={turnaround.arrivalFlight.sibt}
                                            blink={false}
                                            timezone={timezone}
                                            undisplayedZone={true}
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </StyledDataBox>
                            </StyledDataZoneBox>
                            {arrivalFlightOccupation?.occupationStart?.type !== FlightTimeType.S && (
                                <StyledDataZoneBox>
                                    <StyledTitleDataBox>
                                        {arrivalTimeTitle[arrivalFlightOccupation?.occupationStart?.type]}
                                    </StyledTitleDataBox>
                                    <StyledDataBox>
                                        <TimeDisplay
                                            time={getTurnaroundArrivalTime(
                                                turnaround.arrivalFlight,
                                                arrivalFlightOccupation?.occupationStart?.type,
                                            )}
                                            blink={false}
                                            timezone={timezone}
                                            undisplayedZone={true}
                                        />
                                    </StyledDataBox>
                                </StyledDataZoneBox>
                            )}
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>PROV</StyledTitleDataBox>
                                <StyledDataBox>
                                    <IataIcao
                                        iata={turnaround.arrivalFlight.adepIata}
                                        icao={turnaround.arrivalFlight.adepIcao}
                                        fallback="-"
                                    />
                                </StyledDataBox>
                            </StyledDataZoneBox>
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>SERVICE</StyledTitleDataBox>
                                <StyledDataBox>{turnaround.arrivalFlight.arrivalTrafficNature || '-'}</StyledDataBox>
                            </StyledDataZoneBox>
                        </StyledArrivalDataBox>
                    </StyledFlightDataContainer>
                )}
                {turnaround.departureFlight && (
                    <StyledFlightDataContainer>
                        <StyledDateDataBox>
                            {turnaround.multiDaysOccupation && departureFlightDate ? (
                                format(new Date(departureFlightDate), localeToDateFormat[locale])
                            ) : (
                                <>&nbsp;</>
                            )}
                        </StyledDateDataBox>
                        <StyledDepartureDataBox>
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>FL NUMBER</StyledTitleDataBox>
                                <StyledDataBox>
                                    {turnaround.departureFlight.flightNumber
                                        ? (turnaround.departureFlight.airlineIata ||
                                              turnaround.departureFlight.airlineIcao) +
                                          turnaround.departureFlight.flightNumber
                                        : turnaround.departureFlight.callsign
                                        ? turnaround.departureFlight.callsign
                                        : '-'}
                                </StyledDataBox>
                            </StyledDataZoneBox>
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>{departureTimeTitle[FlightTimeType.S]}</StyledTitleDataBox>
                                <StyledDataBox>
                                    {turnaround.departureFlight.sobt ? (
                                        <TimeDisplay
                                            time={turnaround.departureFlight.sobt}
                                            blink={false}
                                            timezone={timezone}
                                            undisplayedZone={true}
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </StyledDataBox>
                            </StyledDataZoneBox>
                            {departureFlightOccupation?.occupationEnd?.type !== FlightTimeType.S && (
                                <StyledDataZoneBox>
                                    <StyledTitleDataBox>
                                        {departureTimeTitle[departureFlightOccupation?.occupationEnd?.type]}
                                    </StyledTitleDataBox>
                                    <StyledDataBox>
                                        <TimeDisplay
                                            time={getTurnaroundDepartureTime(
                                                turnaround.departureFlight,
                                                departureFlightOccupation?.occupationEnd?.type,
                                            )}
                                            blink={false}
                                            timezone={timezone}
                                            undisplayedZone={true}
                                        />
                                    </StyledDataBox>
                                </StyledDataZoneBox>
                            )}
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>DEST</StyledTitleDataBox>
                                <StyledDataBox>
                                    <IataIcao
                                        iata={turnaround.departureFlight.adesIata}
                                        icao={turnaround.departureFlight.adesIcao}
                                        fallback="-"
                                    />
                                </StyledDataBox>
                            </StyledDataZoneBox>
                            <StyledDataZoneBox>
                                <StyledTitleDataBox>SERVICE</StyledTitleDataBox>
                                <StyledDataBox>
                                    {turnaround.departureFlight.departureTrafficNature || '-'}
                                </StyledDataBox>
                            </StyledDataZoneBox>
                        </StyledDepartureDataBox>
                    </StyledFlightDataContainer>
                )}
                {isArrivalOnlyProlongated && (
                    <DeleteTimePicker
                        deleteTime={deleteTime}
                        deleteTimeString={deleteTimeString}
                        minDeleteTime={minDeleteTime}
                        locale={locale}
                        timezone={timezone}
                        onChange={handleDeleteTimeChange}
                    />
                )}
            </StyledFlightDataBox>
            <StyledPaxDataBox>
                <PaxIcon color="#FFFFFF" width="30" height="30" />
                <StyledDataBox>
                    {turnaround.arrivalFlight?.paxCapacity || turnaround.departureFlight?.paxCapacity || '-'}
                </StyledDataBox>
                <PlaneIcon color="#FFFFFF" width="30" height="30" />
                <StyledDataBox>
                    <IataIcao
                        iata={turnaround.arrivalFlight?.acTypeIata || turnaround.departureFlight?.acTypeIata}
                        icao={turnaround.arrivalFlight?.acTypeIcao || turnaround.departureFlight?.acTypeIcao}
                        fallback="-"
                    />
                </StyledDataBox>
                <AirlinesIcon sx={{ color: '#b6d2e5', fontSize: '25px' }} />
                <StyledDataBox>
                    {turnaround.arrivalFlight?.registrationNumber ||
                        turnaround.departureFlight?.registrationNumber ||
                        '-'}
                </StyledDataBox>
            </StyledPaxDataBox>
        </StyledFlightsInformationBox>
    );
});
